/** INTERIORUL UNUI ETAJ pe macheta de situație (Cătălin, 13.10.2026: «să ne
 *  dăm seama dacă e apartament cu 1/2/3 camere și pe ce camere sunt
 *  balcoanele») — partea PURĂ, fără scenă.
 *
 *  Macheta taie clădirea la etajul ales (v. `RandareSit3D`, `interior3d.ts`);
 *  aici e ce se știe despre etaj din planurile lui:
 *    * APARTAMENTELE, cu tipul lor (câte camere: livingul + dormitoarele),
 *      suprafața utilă și balcoanele, fiecare cu camera pe care dă;
 *    * PLANUL ETAJULUI ca desen SVG — pardoselile interioare într-o nuanță
 *      palidă a tipului apartamentului, mobilierul de planșă, ușile (foaia
 *      deschisă și arcul ei), nucleul (lifturile, ghena) și numele cu aria
 *      fiecărei camere — copt apoi într-o singură imagine pe pardoseală;
 *    * COTELE TĂIETURII: de la pardoseala etajului până sub placa de deasupra.
 *
 *  Balcoanele și terasele NU iau culoarea apartamentului: rămân pe pardoseala
 *  lor din machetă (gresie, dale, deck). */
import type { StoreyPlan, StoreyShape } from "../canonical";
import type { Fatada3 } from "./sit3d";

type Pt = [number, number];

/** Încăperile din afară: își păstrează pardoseala din machetă. */
export const EXTERIOARE = new Set(["balcony", "loggia", "terrace", "terasa", "terasa_circulabila"]);

/** Spațiile fără nume pe plan (prea mici, sau tehnice). */
const FARA_NUME = new Set(["lift", "ghena", "tech", "shaft"]);

/** TIPUL APARTAMENTULUI: câte camere are (livingul + dormitoarele). Culoarea
 *  e o nuanță abia vizibilă a pardoselii (Cătălin: «mult mai palidă, să se
 *  distingă mai greu»); `contur` e culoarea întreagă, pentru conturul de sub
 *  mouse. */
export interface TipApartament { camere: 1 | 2 | 3 | 4; fond: string; contur: string }

// palide, dar nu albe: sub lumina machetei (ACES, umbra pereților) nuanțele
// din prototip, aproape albe, ieșeau toate același gri
export const TIPURI_APARTAMENT: readonly TipApartament[] = [
  { camere: 1, fond: "#f5e7c4", contur: "#d99a12" },
  { camere: 2, fond: "#d8ece4", contur: "#2f8f7b" },
  { camere: 3, fond: "#dbe5f6", contur: "#3e6fc0" },
  { camere: 4, fond: "#e8dbf3", contur: "#8350b3" },
];
/** Pardoseala spațiilor comune (holul scării, casa scării, lifturile). */
export const FOND_COMUN = "#ebe8e2";
/** Părțile nucleului, pe pardoseală: casa scării și ghena gri, puțul liftului alb. */
const FOND_NUCLEU: Record<string, string> = { scara: "#e3e1dc", lift: "#f7f6f3", ghena: "#d9d6d0" };
/** Linia ușilor: foaia închisă la culoare, arcul mai deschis, punctat. */
const USA_FOAIE = "#5d6164", USA_ARC = "#8f9396";

export const tipulApartamentului = (camere: number): TipApartament =>
  TIPURI_APARTAMENT[Math.max(0, Math.min(3, camere - 1))];

export interface BalconApartament {
  arie: number;
  /** Felul camerei pe care dă (`living`, `bedroom`…); lipsă = niciuna lipită. */
  spre?: string;
}

export interface ApartamentEtaj {
  eid: string;
  cod: string;
  camere: number;
  /** Suprafața utilă: încăperile interioare. */
  util: number;
  balcoane: BalconApartament[];
  poly: Pt[];
}

// ------------------------------------------------------------------ geometrie
export function aria(p: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}

export function inPoligon(x: number, y: number, p: readonly Pt[]): boolean {
  let r = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i], [xj, yj] = p[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) r = !r;
  }
  return r;
}

export function distContur(x: number, y: number, p: readonly Pt[]): number {
  let m = Infinity;
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / L2));
    m = Math.min(m, Math.hypot(x - a[0] - t * dx, y - a[1] - t * dy));
  }
  return m;
}

/** Cât din conturul lui `a` stă lângă conturul lui `b` (la sub `tol` metri),
 *  în metri — latura comună a două încăperi despărțite de un perete. */
export function laturaComuna(a: readonly Pt[], b: readonly Pt[], tol = 0.45): number {
  let l = 0;
  for (let i = 0; i < a.length; i++) {
    const p = a[i], q = a[(i + 1) % a.length];
    const L = Math.hypot(q[0] - p[0], q[1] - p[1]);
    const k = Math.max(2, Math.ceil(L / 0.25));
    for (let j = 0; j < k; j++) {
      const t = (j + 0.5) / k;
      if (distContur(p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t, b) < tol) l += L / k;
    }
  }
  return l;
}

const arieSpatiu = (s: StoreyShape) => s.area ?? aria(s.poly as Pt[]);
const eExterior = (s: StoreyShape) => EXTERIOARE.has(s.kind) || s.outdoor === "balcony" || s.outdoor === "loggia";

// ------------------------------------------------------------------ apartamente
/** Apartamentele etajului: tipul (livingul + dormitoarele; o garsonieră are o
 *  cameră), suprafața utilă și balcoanele — fiecare cu camera interioară cu
 *  care are cea mai lungă latură comună, adică pe care dă. */
export function apartamenteleEtajului(p: StoreyPlan): ApartamentEtaj[] {
  const pe = new Map<string, StoreyShape[]>();
  for (const s of p.spaces) {
    if (!s.flat) continue;
    (pe.get(s.flat) ?? pe.set(s.flat, []).get(s.flat)!).push(s);
  }
  return p.flatShapes.map((f) => {
    const cs = pe.get(f.eid) ?? [];
    const interior = cs.filter((c) => !eExterior(c));
    const camere = (interior.some((c) => c.kind === "living") ? 1 : 0)
      + interior.filter((c) => c.kind === "bedroom" || c.kind === "dormitor").length;
    const balcoane = cs.filter(eExterior).map((b): BalconApartament => {
      let spre: string | undefined, best = 0;
      for (const c of interior) {
        const l = laturaComuna(b.poly as Pt[], c.poly as Pt[]);
        if (l > best + 1e-6) { best = l; spre = c.kind; }
      }
      return { arie: arieSpatiu(b), ...(spre ? { spre } : {}) };
    });
    return { eid: f.eid, cod: f.label ?? f.eid, camere: Math.max(1, camere),
             util: interior.reduce((s, c) => s + arieSpatiu(c), 0), balcoane, poly: f.poly as Pt[] };
  });
}

// ------------------------------------------------------------------ cotele
/** COTELE TĂIETURII unui etaj pe machetă: pardoseala lui (`jos`) și tăietura,
 *  SUB PLACA DE DEASUPRA (`sus`) — pereții se văd pe toată înălțimea, cu
 *  buiandrugii golurilor, iar placa balconului de deasupra (22 cm sub cota
 *  etajului următor) nu mai rămâne în aer. 30 cm, nu 25: muchia plăcii (brâul
 *  de 25 cm sub cota următoare) ar fi căzut exact în planul tăieturii și ar fi
 *  rămas o felie fără grosime, văzută ca o linie lungă pe fațadă.
 *  Pe mai multe corpuri, cea mai joasă pardoseală și cel mai jos tavan: un
 *  singur plan taie toată macheta. */
export const SUB_PLACA_M = 0.3;

export function cotaEtajului(fatade: readonly Fatada3[], nivel: number): { jos: number; sus: number } | null {
  const ale = fatade.filter((f) => f.nivel === nivel && !f.acoperis && f.inaltime > 0);
  if (!ale.length) return null;
  const jos = Math.min(...ale.map((f) => f.cota));
  const sus = Math.min(...ale.map((f) => f.cota + f.inaltime)) - SUB_PLACA_M;
  return { jos, sus: Math.max(jos + 2, sus) };
}

/** Etajele care se pot alege: cele cu volum pe machetă, de jos în sus. */
export function etajeleMachetei(fatade: readonly Fatada3[]): number[] {
  return [...new Set(fatade.filter((f) => !f.acoperis && f.inaltime > 0 && f.nivel !== undefined)
    .map((f) => f.nivel as number))].sort((a, b) => a - b);
}

// ------------------------------------------------------------------ planul
export interface CadruCamera { cx: number; cy: number; w: number; h: number; deg: number }

export interface PlanEtaj {
  /** Desenul, în metri: `viewBox` = [x0, −y1, W, H] (imaginea are y-ul în jos). */
  svg: string;
  x0: number;
  y0: number;
  W: number;
  H: number;
}

export interface OptiuniPlan {
  /** Cadrul camerei (centru, lățime, adâncime, unghi) — `arhMobilier.cadru`. */
  cadru: (poly: number[][]) => CadruCamera | null;
  /** Mobilierul de planșă al unei camere, ca SVG în cadrul ei (centrat în
   *  origine, `x` pe latura lungă); `null` = cameră nemobilată. */
  mobilier: (kind: string, w: number, h: number) => string | null;
  /** Numele camerei, în limba ecranului. */
  nume: (s: StoreyShape) => string;
  /** Aria, scrisă în limba ecranului («13,2 m²»). */
  arie: (m2: number) => string;
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** PLANUL ETAJULUI ca desen: pardoselile interioare în nuanța tipului de
 *  apartament, mobilierul de planșă, numele și aria camerelor. Exteriorul
 *  (balcoane, terase) rămâne gol — se vede pardoseala machetei prin el.
 *
 *  Geometria se întoarce cu `scale(1,-1)` (lumea are y-ul în sus, imaginea în
 *  jos), deci desenul nu se oglindește față de machetă; textele stau în afara
 *  întoarcerii, ca să se citească drept. */
export function planulEtajului(p: StoreyPlan, apart: readonly ApartamentEtaj[], o: OptiuniPlan): PlanEtaj | null {
  const contur = p.outline as Pt[];
  const pts = [...contur, ...p.spaces.flatMap((s) => s.poly as Pt[])];
  if (pts.length < 3) return null;
  const x0 = Math.min(...pts.map((q) => q[0])) - 0.5, x1 = Math.max(...pts.map((q) => q[0])) + 0.5;
  const y0 = Math.min(...pts.map((q) => q[1])) - 0.5, y1 = Math.max(...pts.map((q) => q[1])) + 0.5;
  const P = (q: readonly number[]) => `${q[0].toFixed(3)},${q[1].toFixed(3)}`;
  const inel = (r: readonly number[][]) => `M${r.map(P).join("L")}Z`;
  const tip = new Map(apart.map((a) => [a.eid, tipulApartamentului(a.camere)]));
  const exterioare = p.spaces.filter(eExterior);
  const geo: string[] = [];
  // fondul etajului: conturul planșeului, cu exteriorul scos din el
  if (contur.length >= 3) {
    geo.push(`<path fill="${FOND_COMUN}" fill-rule="evenodd" d="${inel(contur)}${exterioare.map((s) => inel(s.poly)).join("")}"/>`);
  }
  for (const s of p.spaces) {
    if (eExterior(s)) continue;
    const t = s.flat ? tip.get(s.flat) : undefined;
    geo.push(`<path fill="${t ? t.fond : FOND_COMUN}" d="${inel(s.poly)}"/>`);
  }
  // NUCLEUL: casa scării, lifturile, ghena — pardoseala lor și simbolul de
  // planșă (scara însăși e în 3D, v. `interior3d`)
  for (const c of p.cores) {
    if ((c.poly?.length ?? 0) < 3) continue;
    geo.push(`<path fill="${FOND_NUCLEU[c.kind] ?? FOND_COMUN}" d="${inel(c.poly)}"/>`);
  }
  for (const s of [...p.spaces, ...p.cores]) {
    const k = o.cadru(s.poly as number[][]);
    const m = k ? o.mobilier(s.kind, k.w, k.h) : null;
    if (!k || !m) continue;
    geo.push(`<g transform="translate(${k.cx.toFixed(3)} ${k.cy.toFixed(3)}) rotate(${k.deg.toFixed(2)})">${m}</g>`);
  }
  for (const u of p.openings) {
    const d = usa(u);
    if (d) geo.push(d);
  }
  const texte: string[] = [];
  for (const s of p.spaces) {
    if (FARA_NUME.has(s.kind)) continue;
    const ar = arieSpatiu(s);
    if (ar < 2) continue;
    const k = o.cadru(s.poly as number[][]);
    if (!k) continue;
    const fs = Math.max(0.22, Math.min(0.4, Math.sqrt(ar) * 0.085));
    const x = k.cx.toFixed(3), y = (-k.cy).toFixed(3);
    texte.push(`<text x="${x}" y="${y}" font-size="${fs.toFixed(3)}" text-anchor="middle" fill="#2a2d2e"`
      + ` stroke="#ffffff" stroke-width="${(fs * 0.24).toFixed(3)}" paint-order="stroke" stroke-linejoin="round">`
      + `<tspan x="${x}" dy="-0.1em" font-weight="600">${esc(o.nume(s))}</tspan>`
      + `<tspan x="${x}" dy="1.15em" font-size="${(fs * 0.82).toFixed(3)}">${esc(o.arie(ar))}</tspan></text>`);
  }
  const W = x1 - x0, H = y1 - y0;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${x0.toFixed(3)} ${(-y1).toFixed(3)} ${W.toFixed(3)} ${H.toFixed(3)}"`
    + ` font-family="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif">`
    + `<g transform="scale(1,-1)">${geo.join("")}</g>${texte.join("")}</svg>`;
  return { svg, x0, y0, W, H };
}

/** UȘA PE PLAN: foaia deschisă la 90° din tocul cu balamaua și sfertul ei de
 *  cerc, punctat — aceeași convenție ca planul de nivel (`StoreyView`): în
 *  cadrul golului (`cx`, `cy`, `deg`), foaia spre +y; `balama: end` o pune la
 *  capătul de sfârșit. O ușă dublă are câte o foaie din fiecare toc, de o
 *  jumătate de gol. Ferestrele n-au simbol: le desenează fațada. */
export function usa(o: StoreyShape & { tip?: string }): string | null {
  if (o.tip !== "Door" || o.cx == null || o.cy == null || !o.width_m) return null;
  const l = o.width_m;
  const arc = (hx: number, r: number, oglindit: boolean) => {
    const pts: string[] = [];
    for (let a = 0; a <= 90; a += 7.5) {
      const t = ((oglindit ? 180 - a : a) * Math.PI) / 180;
      pts.push(`${(hx + r * Math.cos(t)).toFixed(3)},${(r * Math.sin(t)).toFixed(3)}`);
    }
    return `<polyline points="${pts.join(" ")}" fill="none" stroke="${USA_ARC}" stroke-width="0.02" stroke-dasharray="0.08 0.06"/>`;
  };
  const foaie = (hx: number, r: number) =>
    `<line x1="${hx.toFixed(3)}" y1="0" x2="${hx.toFixed(3)}" y2="${r.toFixed(3)}" stroke="${USA_FOAIE}" stroke-width="0.035"/>`;
  let desen: string;
  if ((o.foi ?? 1) >= 2) {
    desen = foaie(-l / 2, l / 2) + foaie(l / 2, l / 2) + arc(-l / 2, l / 2, false) + arc(l / 2, l / 2, true);
  } else {
    const sfarsit = o.balama === "end";
    const hx = sfarsit ? l / 2 : -l / 2;
    const f = o.foaie_m && o.foaie_m < l ? o.foaie_m : l;
    desen = foaie(hx, f) + arc(hx, f, sfarsit);
  }
  return `<g transform="translate(${o.cx.toFixed(3)} ${o.cy.toFixed(3)}) rotate(${(o.deg ?? 0).toFixed(2)})">${desen}</g>`;
}

/** Variabilele CSS ale planșei (`var(--mob-linie, #111)`) nu există într-o
 *  imagine: rămân valorile lor implicite. */
export const faraVariabile = (svg: string): string => svg.replace(/var\(--[a-z0-9-]+,\s*([^()]+?)\)/gi, "$1");
